import { useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { currentRubric, type RubricSkill } from '../../store/selectors'
import { LEVEL_LABELS, clampLevel, levelDescriptor } from '../../lib/rubric'
import { formatRelative } from '../../lib/dates'

/**
 * The parent sets the level.
 *
 * Sapling infers levels from a handful of noticed moments; a parent has watched
 * every day of this child's life. So this is not a correction form — it is the
 * higher authority speaking, and the interface says so: the override is
 * labelled as the parent's call, it stays visible, and Sapling's own read is
 * kept beside it rather than overwritten, so handing the skill back is one tap
 * and loses nothing.
 */
export function OverrideControl({
  domainKey,
  skillKey,
  skill,
}: {
  domainKey: string
  skillKey: string
  skill?: RubricSkill
}) {
  const rubricId = useAuroraStore((s: any) => (currentRubric(s) as any)?._id)
  const spineState = useAuroraStore((s: any) =>
    Object.values(s.skillStates ?? {}).find(
      (st: any) => (st.skillKey ?? st.skill_key) === skillKey && !st.deleted,
    ) as any,
  )
  const override = spineState?.parentOverride ?? skill?.parentOverride
  const [note, setNote] = useState(override?.note ?? '')
  const [noteOpen, setNoteOpen] = useState(false)

  if (!rubricId && !spineState) return null

  const current = clampLevel(override ? override.level : skill?.level)
  const inferred = clampLevel(override?.inferredLevel)

  const set = (level: number, withNote = note) => {
    const s = auroraStore.getState() as any
    if (spineState) s.pinSkillLevel({ childId: spineState.childId, skillKey, level, note: withNote.trim() || undefined })
    else s.overrideSkillLevel(rubricId, domainKey, skillKey, level, withNote.trim() || undefined)
  }

  const clear = () => {
    const s = auroraStore.getState() as any
    if (spineState) s.unpinSkillLevel({ childId: spineState.childId, skillKey })
    else s.clearSkillOverride(rubricId, domainKey, skillKey)
    setNote('')
    setNoteOpen(false)
  }

  return (
    <section className={`override${override ? ' is-set' : ''}`}>
      <div className="override-head">
        <h3 className="section-title">{override ? 'Your call' : 'Set it yourself'}</h3>
        {override && (
          <span className="override-when">You set this {formatRelative(override.at)}</span>
        )}
      </div>

      <p className="override-lead">
        {override
          ? 'This is your level, not Sapling’s. It stays until you change it, and everything ' +
            'planned from here works from your number.'
          : 'You have watched more of this than Sapling ever will. If a level looks wrong, set it: ' +
            'yours wins.'}
      </p>

      <div className="override-scale" role="group" aria-label="Set the level">
        {[1, 2, 3, 4, 5].map((level) => {
          const active = current === level
          const descriptor = levelDescriptor(domainKey, skillKey, level)?.descriptor
          return (
            <button
              key={level}
              type="button"
              className={`override-step${active ? ' is-active' : ''}`}
              aria-pressed={active}
              aria-label={`Level ${level}, ${LEVEL_LABELS[level as 1]}${
                descriptor ? `. ${descriptor}` : ''
              }`}
              onClick={() => (active && override ? clear() : set(level))}
            >
              <span className="override-step-num tnum">{level}</span>
              <span className="override-step-name">{LEVEL_LABELS[level as 1]}</span>
            </button>
          )
        })}
      </div>

      {override && (
        <div className="override-foot">
          {inferred && inferred !== override.level ? (
            <button className="btn btn-ghost btn-sm" onClick={clear}>
              Use Sapling’s read again ({inferred})
            </button>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={clear}>
              Clear my override
            </button>
          )}

          {noteOpen || override.note ? (
            <label className="override-note">
              <span className="sr-only">Why you set this level</span>
              <input
                className="override-note-input"
                value={note}
                placeholder="Why? (optional, Sapling will read it)"
                onChange={(e) => setNote(e.target.value)}
                onBlur={() => note.trim() !== (override.note ?? '') && set(override.level)}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              />
            </label>
          ) : (
            <button className="btn btn-quiet btn-sm" onClick={() => setNoteOpen(true)}>
              Add a reason
            </button>
          )}
        </div>
      )}
    </section>
  )
}
